import React from 'react';
import { Button, Form } from 'react-bootstrap';

const PAGE_SIZES = [25, 50, 100, 200];

// Page window around the current page, so 16 pages don't render 16 buttons.
const pageWindow = (page, pages, span = 2) => {
  const start = Math.max(1, Math.min(page - span, pages - span * 2));
  const end = Math.min(pages, Math.max(page + span, span * 2 + 1));
  const out = [];
  for (let p = start; p <= end; p += 1) out.push(p);
  return out;
};

/**
 * Pager + page-size control for any list screen.
 *
 * It started life on the invoices list and now serves partners and operators
 * too. One pager, so a page turn means the same thing wherever the office
 * meets it: the same arrows in the same place, the same "Showing 26–50 of
 * 158" to check a filter against, and the same page sizes to choose from.
 *
 * `pagination` is deliberately the API's own envelope — `{ page, limit,
 * total, pages }` — so a server-paged screen can hand its response straight
 * in, and a client-paged one only has to count its own rows into the same
 * four numbers.
 */
function ListPager({ pagination, onPageChange, onLimitChange }) {
  const {
    page, limit, total, pages,
  } = pagination;
  if (total === 0) return null;

  const first = (page - 1) * limit + 1;
  const last = Math.min(page * limit, total);

  return (
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
      <div className="text-muted small">
        {`Showing ${first}–${last} of ${total}`}
      </div>

      <div className="d-flex align-items-center gap-2">
        <Form.Select
          size="sm"
          style={{ width: 'auto' }}
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          aria-label="Rows per page"
        >
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{`${n} / page`}</option>)}
        </Form.Select>

        <Button
          size="sm"
          variant="outline-secondary"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          <i className="fa fa-chevron-left" />
        </Button>

        {pages > 1 && pageWindow(page, pages).map((p) => (
          <Button
            key={p}
            size="sm"
            variant={p === page ? 'primary' : 'outline-secondary'}
            onClick={() => onPageChange(p)}
          >
            {p}
          </Button>
        ))}

        <Button
          size="sm"
          variant="outline-secondary"
          disabled={page >= pages}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          <i className="fa fa-chevron-right" />
        </Button>
      </div>
    </div>
  );
}

export default ListPager;
